// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { ProductListProductQuery } from "@werner/Overrides/Widgets/ProductList/ProductListProductCount";
import { DivWrapper, SharedProductPagesContexts } from "@werner/XDL/constants";
import { color, hideElement, xScreen } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    totalItemCount: getProductListDataViewProperty(state, "pagination")?.totalItemCount,
});

type Props = ReturnType<typeof mapStateToProps> & HasCategoryContext & WidgetProps;

export interface ProductListLayoutStyles {
    container?: InjectableCss;
    productListContainer?: InjectableCss;
    headerWrapper?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    mainSectionActionsContainer?: InjectableCss;
    filterMobileSectionContainer?: InjectableCss;
    sortFilterButton?: ButtonPresentationProps;
    sortAndPaginationWrapper?: InjectableCss;
    bottomPagination?: InjectableCss;
    modal?: ModalPresentationProps;
}

export const layoutStyles: ProductListLayoutStyles = {
    productListContainer: {
        css: css`
            display: flex;
            gap: 54px;
        `,
    },
    headerWrapper: {
        css: css`
            margin-bottom: 58px;
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 217px;
            ${xScreen("lte", "md")} {
                &.hide-on-mobile {
                    ${hideElement}
                }
                flex: 0 0 100%;
            }

            & > ${DivWrapper} {
                display: flex;
                flex-direction: column;
                gap: 20px;
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        `,
    },
    mainSectionActionsContainer: {
        css: css`
            display: flex;
            gap: 20px;
            justify-content: space-between;
            padding-top: 11px;
            border-top: 1px solid ${color("common.border")};
            flex-wrap: wrap;
            ${({ isShellEditing }: any) =>
                isShellEditing &&
                css`
                    flex-wrap: wrap;
                `}
        `,
    },
    sortAndPaginationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 70px;
            justify-content: flex-end;

            ${xScreen("lte", "md")} {
                &.hide-on-mobile {
                    ${hideElement}
                }

                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 20px;
            }
        `,
    },
    filterMobileSectionContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            gap: 30px;
            ${xScreen("gt", "lg")} {
                ${hideElement}
            }
        `,
    },
};

const styles = layoutStyles;

const ProductListLayout: FC<Props> = ({ totalItemCount, id, category }) => {
    const { isEditing } = useShellContext();
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.headerWrapper} data-test-selector="header-productList">
                <XngZone contentId={id} zoneName="ContentMainHeader00" hint="Title" normalWidth />
                {category && (
                    <XngZone
                        contentId={id}
                        zoneName={`ContentMainHeader${category.shortDescription ?? category.name}`}
                        hint={`Zone for ${category.shortDescription ?? category.name}`}
                        normalWidth
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.productListContainer}>
                <StyledWrapper {...styles.leftColumnContainer} className="hide-on-mobile"></StyledWrapper>
                <StyledWrapper {...styles.rightColumnContainer}>
                    <ProductListProductQuery />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.productListContainer}>
                <StyledWrapper {...styles.leftColumnContainer} className="hide-on-mobile">
                    <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumnContainer}>
                    {!!totalItemCount && (
                        <StyledWrapper {...styles.mainSectionActionsContainer} {...{ isShellEditing: isEditing }}>
                            <XngZone contentId={id} zoneName="productsCount" normalWidth={true} />
                            <StyledWrapper {...styles.sortAndPaginationWrapper} className="hide-on-mobile">
                                <XngZone contentId={id} zoneName="sortItem" normalWidth={true} />
                                <XngZone contentId={id} zoneName="PaginationPerPage" normalWidth={true} />
                                <XngZone contentId={id} zoneName="PaginationPage" normalWidth={true} />
                                <XngZone contentId={id} zoneName="ViewItem" normalWidth={true} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.filterMobileSectionContainer}>
                                <Button
                                    onClick={modalOpenHandler}
                                    {...styles.sortFilterButton}
                                    aria-label="Sort & Filter"
                                >
                                    {translate("Sort & Filter")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                    <StyledWrapper {...styles.bottomPagination}>
                        <XngZone contentId={id} zoneName="PaginationPage" normalWidth={true} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Sort & Filter")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer gap={24}>
                    <GridItem width={12}>
                        <StyledWrapper {...styles.sortAndPaginationWrapper}>
                            <XngZone contentId={id} zoneName="sortItem" />
                            <XngZone contentId={id} zoneName="PaginationPerPage" />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem width={12}>
                        <StyledWrapper {...styles.leftColumnContainer}>
                            <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListLayout)),
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        allowedContexts: [...SharedProductPagesContexts] as any,
    },
};

export default widgetModule;
